/**
 * frontend/src/pages/Templates.js
 *
 * Templates management page.
 * Allows workspace admins to create, edit, and delete change request templates.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Alert } from 'react-bootstrap';
import { motion } from 'framer-motion';
import {
  FiFileText, FiPlus, FiEdit2, FiTrash2, FiArrowLeft, FiSave,
  FiBold, FiItalic, FiUnderline, FiList, FiCode
} from 'react-icons/fi';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { formatErrorMessage } from '../utils/errorHandling';
import Sidebar from '../components/Layout/Sidebar';
import Header from '../components/Layout/Header';

const pageStyles = {
  pageWrapper: {
    minHeight: '100vh',
    background: '#f8fafc',
  },
  mainContent: {
    marginLeft: '72px',
    marginTop: '64px',
    minHeight: 'calc(100vh - 64px)',
    padding: '32px 48px',
  },
  contentWrapper: {
    maxWidth: '1200px',
  },
  backButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    color: '#64748b',
    textDecoration: 'none',
    fontSize: '0.9rem',
    marginBottom: '24px',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
  },
  pageTitle: {
    fontSize: '1.75rem',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  pageSubtitle: {
    color: '#64748b',
    marginBottom: '32px',
  },
  sectionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '24px',
  },
  section: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    border: '1px solid #e2e8f0',
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  templateCard: {
    background: '#f8fafc',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '12px',
    border: '1px solid #e2e8f0',
    transition: 'all 0.2s',
    cursor: 'pointer',
  },
  templateCardHover: {
    borderColor: '#6366f1',
    background: '#fafbff',
  },
  templateHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'start',
    marginBottom: '8px',
  },
  templateName: {
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '4px',
  },
  templateMeta: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  badge: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '600',
  },
  badgeDefault: {
    background: '#dbeafe',
    color: '#1e40af',
  },
  badgeCustom: {
    background: '#f3e8ff',
    color: '#6b21a8',
  },
  changeTypeBadge: {
    background: '#e0f2fe',
    color: '#0369a1',
  },
  templateActions: {
    display: 'flex',
    gap: '8px',
  },
  iconButton: {
    background: 'none',
    border: 'none',
    padding: '6px',
    cursor: 'pointer',
    color: '#64748b',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  editorWrapper: {
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  editorToolbar: {
    display: 'flex',
    gap: '4px',
    padding: '8px 12px',
    borderBottom: '1px solid #e2e8f0',
    background: '#f8fafc',
    flexWrap: 'wrap',
  },
  toolbarButton: {
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'transparent',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#64748b',
    transition: 'all 0.15s ease',
  },
  toolbarButtonActive: {
    background: '#6366f1',
    color: 'white',
  },
  toolbarDivider: {
    width: '1px',
    height: '24px',
    background: '#e2e8f0',
    margin: '4px 8px',
  },
  editor: {
    background: 'white',
    minHeight: '200px',
  },
  editorContent: {
    minHeight: '180px',
    padding: '16px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#64748b',
  },
  formActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '16px',
  },
};

const changeTypeOptions = [
  { value: 'policy', label: 'Policy' },
  { value: 'procedure', label: 'Procedure' },
  { value: 'documentation', label: 'Documentation' },
  { value: 'infrastructure', label: 'Infrastructure' },
  { value: 'network', label: 'Network' },
  { value: 'security', label: 'Security' },
  { value: 'cloud', label: 'Cloud Resources' },
  { value: 'deployment', label: 'Deployment' },
  { value: 'ci_cd', label: 'CI/CD Pipeline' },
  { value: 'kubernetes', label: 'Kubernetes' },
  { value: 'docker', label: 'Docker/Containers' },
  { value: 'monitoring', label: 'Monitoring/Alerting' },
  { value: 'logging', label: 'Logging' },
  { value: 'database', label: 'Database' },
  { value: 'schema', label: 'Schema Migration' },
  { value: 'data', label: 'Data Migration' },
  { value: 'api', label: 'API' },
  { value: 'frontend', label: 'Frontend' },
  { value: 'backend', label: 'Backend' },
  { value: 'configuration', label: 'Configuration' },
  { value: 'feature_flag', label: 'Feature Flag' },
];

// Editor Toolbar Component
const EditorToolbar = ({ editor }) => {
  if (!editor) return null;

  const ToolbarBtn = ({ onClick, isActive, children, title }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        ...pageStyles.toolbarButton,
        ...(isActive ? pageStyles.toolbarButtonActive : {}),
      }}
      onMouseEnter={(e) => {
        if (!isActive) e.target.style.background = '#e2e8f0';
      }}
      onMouseLeave={(e) => {
        if (!isActive) e.target.style.background = 'transparent';
      }}
    >
      {children}
    </button>
  );

  return (
    <div style={pageStyles.editorToolbar}>
      <ToolbarBtn
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
        title="Bold"
      >
        <FiBold size={16} />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
        title="Italic"
      >
        <FiItalic size={16} />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        isActive={editor.isActive('underline')}
        title="Underline"
      >
        <FiUnderline size={16} />
      </ToolbarBtn>

      <div style={pageStyles.toolbarDivider}></div>

      <ToolbarBtn
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
        title="Bullet List"
      >
        <FiList size={16} />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
        title="Numbered List"
      >
        <span style={{ fontSize: '14px', fontWeight: '600' }}>1.</span>
      </ToolbarBtn>

      <div style={pageStyles.toolbarDivider}></div>

      <ToolbarBtn
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        isActive={editor.isActive('codeBlock')}
        title="Code Block"
      >
        <FiCode size={16} />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={editor.isActive('blockquote')}
        title="Quote"
      >
        <span style={{ fontSize: '16px', fontWeight: '700', lineHeight: 1 }}>"</span>
      </ToolbarBtn>
    </div>
  );
};

const Templates = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    currentWorkspace,
    templates,
    fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  } = useWorkspace();

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    title_template: '',
    change_type: 'policy',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Placeholder.configure({
        placeholder: 'Enter template description with placeholders like [describe the change]...',
      }),
    ],
    content: '',
    editorProps: {
      attributes: {
        style: 'outline: none; min-height: 180px;',
      },
    },
  });

  useEffect(() => {
    if (!currentWorkspace) {
      navigate('/dashboard');
      return;
    }

    if (currentWorkspace.user_role !== 'admin') {
      navigate('/dashboard');
      return;
    }

    fetchTemplates(currentWorkspace.id).catch((err) => {
      setError(formatErrorMessage(err));
    });
  }, [currentWorkspace, navigate, fetchTemplates]);

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setIsEditing(true);
    setFormData({
      name: template.name,
      title_template: template.title_template || '',
      change_type: template.change_type,
    });
    editor?.commands.setContent(template.description_template || '');
    setError('');
    setSuccess('');
  };

  const handleNewTemplate = () => {
    setSelectedTemplate(null);
    setIsEditing(false);
    setFormData({
      name: '',
      title_template: '',
      change_type: 'policy',
    });
    editor?.commands.setContent('');
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.name.trim()) {
      setError('Template name is required');
      return;
    }

    try {
      const templateData = {
        name: formData.name,
        title_template: formData.title_template,
        description_template: editor?.getHTML() || '',
        change_type: formData.change_type,
      };

      if (isEditing && selectedTemplate) {
        await updateTemplate(selectedTemplate.id, templateData);
        setSuccess('Template updated successfully');
      } else {
        await createTemplate(currentWorkspace.id, templateData);
        setSuccess('Template created successfully');
        handleNewTemplate();
      }
    } catch (err) {
      setError(formatErrorMessage(err));
    }
  };

  const handleDelete = async (template) => {
    if (template.is_default) {
      setError('Cannot delete default templates');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${template.name}"?`)) {
      return;
    }

    try {
      await deleteTemplate(template.id);
      setSuccess('Template deleted successfully');
      if (selectedTemplate?.id === template.id) {
        handleNewTemplate();
      }
    } catch (err) {
      setError(formatErrorMessage(err));
    }
  };

  return (
    <div style={pageStyles.pageWrapper}>
      <Header />
      <Sidebar workspace={currentWorkspace} />

      <main style={pageStyles.mainContent}>
        <div style={pageStyles.contentWrapper}>
          <button
            style={pageStyles.backButton}
            onClick={() => navigate('/dashboard')}
          >
            <FiArrowLeft size={16} />
            Back to Dashboard
          </button>

          <h1 style={pageStyles.pageTitle}>
            <FiFileText />
            Change Templates
          </h1>
          <p style={pageStyles.pageSubtitle}>
            Create reusable templates for common change requests
          </p>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

          <div style={pageStyles.sectionsGrid}>
            {/* Left: Template List */}
            <div style={pageStyles.section}>
              <div style={pageStyles.sectionTitle}>
                <FiFileText size={18} />
                Templates
              </div>

              <Button
                variant="primary"
                size="sm"
                onClick={handleNewTemplate}
                className="mb-3 w-100"
              >
                <FiPlus size={16} /> New Template
              </Button>

              {templates.length === 0 ? (
                <div style={pageStyles.emptyState}>
                  <FiFileText size={48} style={{ marginBottom: '12px' }} />
                  <p>No templates yet</p>
                </div>
              ) : (
                templates.map((template) => (
                  <motion.div
                    key={template.id}
                    style={{
                      ...pageStyles.templateCard,
                      ...(hoveredCard === template.id ? pageStyles.templateCardHover : {}),
                      ...(selectedTemplate?.id === template.id ? { borderColor: '#6366f1', background: '#fafbff' } : {}),
                    }}
                    onMouseEnter={() => setHoveredCard(template.id)}
                    onMouseLeave={() => setHoveredCard(null)}
                    onClick={() => handleEditTemplate(template)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div style={pageStyles.templateHeader}>
                      <div>
                        <div style={pageStyles.templateName}>{template.name}</div>
                        <div style={pageStyles.templateMeta}>
                          <span
                            style={{
                              ...pageStyles.badge,
                              ...(template.is_default ? pageStyles.badgeDefault : pageStyles.badgeCustom),
                            }}
                          >
                            {template.is_default ? 'Default' : 'Custom'}
                          </span>
                          <span style={{ ...pageStyles.badge, ...pageStyles.changeTypeBadge }}>
                            {changeTypeOptions.find(o => o.value === template.change_type)?.label}
                          </span>
                        </div>
                      </div>
                      <div style={pageStyles.templateActions} onClick={(e) => e.stopPropagation()}>
                        <button
                          style={pageStyles.iconButton}
                          onClick={() => handleEditTemplate(template)}
                          title="Edit"
                        >
                          <FiEdit2 size={16} />
                        </button>
                        {!template.is_default && (
                          <button
                            style={{ ...pageStyles.iconButton, color: '#ef4444' }}
                            onClick={() => handleDelete(template)}
                            title="Delete"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {/* Right: Template Form */}
            <div style={pageStyles.section}>
              <div style={pageStyles.sectionTitle}>
                {isEditing ? 'Edit Template' : 'Create Template'}
              </div>

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Template Name *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., AWS Infrastructure Change"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Title Template</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., AWS Change: [Resource Type]"
                    value={formData.title_template}
                    onChange={(e) => setFormData({ ...formData, title_template: e.target.value })}
                  />
                  <Form.Text className="text-muted">
                    Use [placeholders] for users to fill in
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Change Type *</Form.Label>
                  <Form.Select
                    value={formData.change_type}
                    onChange={(e) => setFormData({ ...formData, change_type: e.target.value })}
                    required
                  >
                    {changeTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Description Template</Form.Label>
                  <div style={pageStyles.editorWrapper}>
                    <EditorToolbar editor={editor} />
                    <div style={pageStyles.editorContent}>
                      <EditorContent editor={editor} />
                    </div>
                  </div>
                  <Form.Text className="text-muted">
                    Use [placeholders] and formatting to create a reusable template
                  </Form.Text>
                </Form.Group>

                <div style={pageStyles.formActions}>
                  <Button type="submit" variant="primary">
                    <FiSave size={16} /> {isEditing ? 'Update Template' : 'Create Template'}
                  </Button>
                  {isEditing && (
                    <Button variant="outline-secondary" onClick={handleNewTemplate}>
                      Cancel
                    </Button>
                  )}
                </div>
              </Form>
            </div>
          </div>
        </div>
      </main>

      {/* Editor Styles */}
      <style>{`
        .ProseMirror {
          outline: none;
          min-height: 180px;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #94a3b8;
          pointer-events: none;
          height: 0;
        }
        .ProseMirror p {
          margin-bottom: 0.75em;
        }
        .ProseMirror ul, .ProseMirror ol {
          padding-left: 1.5em;
          margin-bottom: 0.75em;
        }
        .ProseMirror li {
          margin-bottom: 0.25em;
        }
        .ProseMirror blockquote {
          border-left: 3px solid #6366f1;
          padding-left: 1em;
          margin-left: 0;
          color: #64748b;
          font-style: italic;
        }
        .ProseMirror pre {
          background: #1e293b;
          color: #e2e8f0;
          padding: 12px 16px;
          border-radius: 8px;
          font-family: 'Monaco', 'Menlo', monospace;
          font-size: 0.875rem;
          overflow-x: auto;
        }
        .ProseMirror code {
          background: #f1f5f9;
          padding: 2px 6px;
          border-radius: 4px;
          font-family: 'Monaco', 'Menlo', monospace;
          font-size: 0.875em;
        }
        .ProseMirror pre code {
          background: none;
          padding: 0;
        }
      `}</style>
    </div>
  );
};

export default Templates;
